import { useCallback, useEffect, useRef, useState } from 'react';
import { IntelligenceUnavailableError, StreamHandlers } from './intelligenceApi';
import { IntelligenceEvent } from './types';
import { extractEntityIds } from './components/IntelText';

export type StreamStatus = 'idle' | 'running' | 'done' | 'error' | 'unavailable';

export interface StreamState<R> {
  status: StreamStatus;
  text: string;
  progress: string[];
  result: R | null;
  error: string | null;
  entityIds: string[];
}

const INITIAL = { status: 'idle' as StreamStatus, text: '', progress: [], result: null, error: null, entityIds: [] };

/**
 * Runs one AI stream at a time (starting a new one aborts the previous one) and exposes
 * accumulated text, progress lines, final result and referenced entity ids.
 */
export function useIntelligenceStream<Req, R = any>(
  runner: (request: Req, handlers: StreamHandlers) => Promise<void>,
) {
  const [state, setState] = useState<StreamState<R>>(INITIAL);
  const controllerRef = useRef<AbortController | null>(null);

  const stop = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    setState((s) => (s.status === 'running' ? { ...s, status: s.text ? 'done' : 'idle' } : s));
  }, []);

  const reset = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    setState(INITIAL);
  }, []);

  const start = useCallback(
    async (request: Req) => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      setState({ ...INITIAL, status: 'running' });
      let failed = false;
      const onEvent = (event: IntelligenceEvent) => {
        if (controller.signal.aborted) return;
        // Flag synchronously: state updaters run later, after the stream may already have finished
        if (event.type === 'error') failed = true;
        setState((s) => {
          switch (event.type) {
            case 'progress':
              return { ...s, progress: [...s.progress, event.message] };
            case 'content': {
              const text = s.text + event.text;
              return { ...s, text, entityIds: extractEntityIds(text) };
            }
            case 'result':
              return { ...s, result: event.data as R };
            case 'error':
              return { ...s, status: 'error', error: event.message };
            default:
              return s;
          }
        });
      };
      try {
        await runner(request, { onEvent, signal: controller.signal });
        if (!controller.signal.aborted && !failed) setState((s) => (s.status === 'error' ? s : { ...s, status: 'done' }));
      } catch (error: any) {
        if (controller.signal.aborted || error?.name === 'AbortError') return;
        setState((s) => ({
          ...s,
          status: error instanceof IntelligenceUnavailableError ? 'unavailable' : 'error',
          error: error?.message || 'Something went wrong',
        }));
      } finally {
        if (controllerRef.current === controller) controllerRef.current = null;
      }
    },
    [runner],
  );

  useEffect(() => () => controllerRef.current?.abort(), []);

  return { ...state, start, stop, reset };
}
